import { NO_TRANSLATE_CLASS } from "@flat-ui";
import { Stack } from "@mui-wrapper";
import { DevFeaturesTableRow } from "@components/dev-features-dialog/dev-features-table-row";
import {
  FEATURES,
  FeatureToggle,
  DevToolItem,
  DEV_TOOLS,
  ButtonsCallback,
  DevToolKey,
} from "@components/dev-features-dialog/features";
import { useDevTools } from "@components/dev-features-dialog/use-dev-tools";
import { useFeatureToggle } from "@components/dev-features-dialog/use-feature-toggle";
import { useCallback, useMemo } from "react";
import { useAppSelector } from "@store/store-helper";
import { AppState, appStateSelector } from "@store/app/app-slice";
import { DevToolsTableRow } from "@components/dev-features-dialog/dev-tools-table-row";
import { isToolToggleItem } from "@components/dev-features-dialog/features-utils";
import { devFeaturesStateSelector } from "@store/dev-features/dev-features-slice";
import { DevFeaturesTable } from "@components/dev-features-dialog/dev-features-table";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { ExperimentalFeatureEvents } from "@utils/track-event/track-event-list";

/** Union of all trackable event types in dev tools dialog */
type TrackableEventType = DevToolKey | keyof AppState;

/** Valid experimental feature event values for tracking */
type TrackableEventValue =
  | ExperimentalFeatureEvents.setDevMode
  | ExperimentalFeatureEvents.setBetaMode
  | ExperimentalFeatureEvents.setShowAllPlans
  | ExperimentalFeatureEvents.setShowAllProducts
  | ExperimentalFeatureEvents.setShowFullDateTime
  | ExperimentalFeatureEvents.enableSentry
  | ExperimentalFeatureEvents.setShowHardwareLicense
  | ExperimentalFeatureEvents.setAllowBulkOperations;

/**
 * Mapping from app state keys and dev tool keys to their corresponding events.
 */
const EVENT_MAPPING: Record<TrackableEventType, TrackableEventValue> = {
  isDevModeEnabled: ExperimentalFeatureEvents.setDevMode,
  isBetaTestingEnabled: ExperimentalFeatureEvents.setBetaMode,
  showAllPlans: ExperimentalFeatureEvents.setShowAllPlans,
  showAllProducts: ExperimentalFeatureEvents.setShowAllProducts,
  showFullDateTime: ExperimentalFeatureEvents.setShowFullDateTime,
  showHardwareLicense: ExperimentalFeatureEvents.setShowHardwareLicense,
  allowBulkOperations: ExperimentalFeatureEvents.setAllowBulkOperations,
  enableSentry: ExperimentalFeatureEvents.enableSentry,
};

/**
 * Panel displaying development features and tools tables.
 */
export function DevFeaturesPanel(): React.JSX.Element {
  const devTools = useDevTools();
  const { trackAsyncEvent } = useTrackEvent();
  const { createToggleHandler } = useFeatureToggle();
  const appState = useAppSelector(appStateSelector);
  const devFeaturesState = useAppSelector(devFeaturesStateSelector);

  /** Track amplitude event for dev tools and features */
  const trackAmplitudeEvent = useCallback(
    async (
      eventType: DevToolKey | keyof AppState,
      isEnabled: boolean
    ): Promise<void> => {
      const eventName = EVENT_MAPPING[eventType];

      await trackAsyncEvent({
        name: eventName,
        props: { isEnabled },
      });
    },
    [trackAsyncEvent]
  );

  const allFeatures: FeatureToggle[] = useMemo(
    () =>
      FEATURES.map((feature) => ({
        ...feature,
        isEnabled: appState[feature.appStateKey],
        onToggle: async (isEnabled: boolean) => {
          await trackAmplitudeEvent(feature.appStateKey, isEnabled);

          const toggleHandler = createToggleHandler(feature);
          toggleHandler(isEnabled);
        },
      })),
    [appState, createToggleHandler, trackAmplitudeEvent]
  );

  const allTools = useMemo<DevToolItem[]>(
    () =>
      DEV_TOOLS.map((tool) => {
        if (isToolToggleItem(tool) && tool.featureStateKey !== undefined) {
          return {
            ...tool,
            isEnabled: devFeaturesState[tool.featureStateKey],
            onToggle: async (isEnabled: boolean) => {
              await trackAmplitudeEvent(tool.action, isEnabled);
              devTools[tool.action](isEnabled);
            },
          };
        }
        return {
          ...tool,
          onClick: devTools[tool.action] as ButtonsCallback,
        };
      }),
    [devFeaturesState, devTools, trackAmplitudeEvent]
  );

  return (
    <Stack
      className={NO_TRANSLATE_CLASS}
      sx={{ width: "100%", height: "100%", gap: 4 }}
    >
      {/* Features Table */}
      <DevFeaturesTable
        title="Features"
        items={allFeatures}
        rowComponent={DevFeaturesTableRow}
      />

      {/* Dev Tools Table */}
      <DevFeaturesTable
        title="Dev Tools"
        items={allTools}
        rowComponent={DevToolsTableRow}
      />
    </Stack>
  );
}
